// lib/cascade/phase-a-runs.js
// ═══════════════════════════════════════════════════════════
// PHASE A RUN HISTORY + NEXT MOVES
//
// Source-of-truth:
//   docs/CONTINUUM_H_3V2_PHASE_A_RUN.md (verdict on H-3v2)
//   docs/CONTINUUM_PHASE_5_SOURCE_AUDIT.md (next moves + Phase 7 sequence)
//   docs/CONTINUUM_HYPOTHESIS_FORMAT.md §X (priority anchors)
// ═══════════════════════════════════════════════════════════

// ─── Phase A run history ─────────────────────────────────────
// Ordered with most-recent first.
export const PHASE_A_RUNS = [
  {
    id: 'batch-1-2026-05-06',
    hypothesisId: 'BATCH (H-2v2, H-5, H-6, H-7, H-8, H-9, H-13, H-14, H-16)',
    hypothesisTitle: 'Phase A Batch 1 — 9 hypotheses run with available evidence',
    runDate: '2026-05-06',
    runLabel: 'Batch run on retrofitted + Tier 1 + cascade-integration hypotheses',
    verdict: 'mixed',
    isLiveRun: true,
    keyDataPoint: 'Net: 4 SURVIVED, 4 SURVIVED-RESPECIFIED, 1 KILLED, 1 INCONCLUSIVE',
    summary: 'Batch Phase A red-team on 9 of 15 pending hypotheses. Used available evidence (UN WPP 2024, OECD SOCX, LPI dataset, training-knowledge for narrative-architecture coding). Three structural-attractor hypotheses now retired across the cascade integration plan (H-3, H-3v3, and now H-7) — the framework consistently rejects "different cases converge to same TFR floor" framings. Refugia framework (H-13 + H-16 + narrowed H-2v2/H-6) consolidates with Israel TFR 2.31 by 2050 as the strongest empirical anchor. Cultural-mechanism cases (H-5 Iran, H-8 Quebec) both SURVIVED, supporting H-Continuum-11\'s Cultural-layer-necessity framing.',
    artifact: 'docs/CONTINUUM_PHASE_A_BATCH_1.md',
    batchVerdicts: [
      { id: 'H-Continuum-2v2', verdict: 'survived-respecified', short: 'Estonia counter-case wounds general-civic-arch claim; survives in narrowed existential-threat form' },
      { id: 'H-Continuum-5',   verdict: 'survived',             short: 'Iran joint-deployment cleanly supported by within-case longitudinal evidence' },
      { id: 'H-Continuum-6',   verdict: 'survived-respecified', short: 'Refugia framing (ultra-orthodox + religious-Zionist), not population-wide moderator' },
      { id: 'H-Continuum-7',   verdict: 'killed',               short: 'Korea 2050 TFR 1.03 vs Italy 1.35; 0.32-gap divergence, not convergence; retires' },
      { id: 'H-Continuum-8',   verdict: 'survived',             short: 'Within-society DiD supports cultural-transmission as Quebec dominant mechanism' },
      { id: 'H-Continuum-9',   verdict: 'inconclusive',          short: '~4-5 coupled clusters qualitatively supported; ≥6 threshold needs formal VAR test' },
      { id: 'H-Continuum-13',  verdict: 'survived-respecified', short: 'Cod case shows refugia + connectivity *to the affected population* matters most' },
      { id: 'H-Continuum-14',  verdict: 'survived',             short: 'H-4 22.4% recovery base rate already supports; eco hysteresis is empirically modal' },
      { id: 'H-Continuum-16',  verdict: 'survived',             short: 'Refugia present in Hungary (Roma) + US Mormon comparison; Israel 2.31 anchor' },
    ],
    incidentalSupport: [
      { hypothesisId: 'H-Continuum-11', signal: 'Cultural-layer-necessity strengthened by Iran (H-5) + Quebec (H-8) cultural-mechanism survivals' },
      { hypothesisId: 'H-Continuum-21 (proto)', signal: 'Korea-Italy 0.32-gap divergence (H-7 killer) is direct support for metapopulation-structure claim' },
    ],
    blockedNotRun: [
      { hypothesisId: 'H-Continuum-10', blocker: 'Cultural-layer signal — needs GDELT data run' },
      { hypothesisId: 'H-Continuum-11', blocker: 'Cultural-layer signal — needs GDELT data run' },
      { hypothesisId: 'H-Continuum-12', blocker: 'Species-cultural-attitude literature review needed' },
      { hypothesisId: 'H-Continuum-15 (KEYSTONE)', blocker: 'Needs the others first; partial evidence accumulating' },
      { hypothesisId: 'H-Continuum-17', blocker: 'KOSIS sub-regional + cooperative-breeding-density composite proxy needed' },
      { hypothesisId: 'H-Continuum-18', blocker: 'Cultural-layer signal — needs GDELT data run' },
    ],
  },
  {
    id: 'h-3v2-rerun-2024-2026-05-05',
    hypothesisId: 'H-Continuum-3v2',
    hypothesisTitle: 'Late-demographic-transition timing as the actual mechanism',
    runDate: '2026-05-05',
    runLabel: 'Re-run on UN WPP 2024 data',
    verdict: 'survived',
    isLiveRun: true,
    keyDataPoint: 'UN WPP 2024 gap: 0.45 (inconclusive zone — between 0.3 and 0.5 thresholds)',
    summary: 'Discrete second Phase A run on H-3v2 against live UN WPP 2024 medium-variant data (44,857 rows from scripts/pull-un-wpp.js). Three-voice red-team re-evaluated: late-transition timing shifts from "partial" to dominant explanation; structural features become case-specific (Oman + Israel) rather than general; Counter-Narrative #1 (pattern-breakers as early-cliff) gains HIGH strength as empirically demonstrated; Counter-Narrative #3 (data-artifact) is VINDICATED. Outside View shifts: 1 supports, 2 partial-support-with-case-specificity, 1 neutral. H-3v2 SURVIVES on 2024 data; H-3v3 retires (its respec motivation didn\'t hold under fresh data).',
    artifact: 'docs/CONTINUUM_H_3V2_PHASE_A_RERUN_2024.md',
    incidentalSupport: [
      { hypothesisId: 'H-Continuum-13', signal: 'Oman + Israel — eco refugia + connectivity case-specific support' },
      { hypothesisId: 'H-Continuum-16', signal: 'Israel TFR 2.31 by 2050 — strongest demographic-refugia anchor in dataset' },
      { hypothesisId: 'H-Continuum-2v2', signal: 'Israel narrative-architecture signal' },
    ],
    modules: {
      rootCause: {
        title: 'Root-Cause Engine (re-evaluated)',
        question: 'What is the actual cause of the maintained 0.45 gap?',
        candidates: [
          {
            label: 'A — Late-transition timing (the registered claim)',
            verdict: 'plausible',
            reasoning: 'STRONGER SUPPORT than 2022 run. Pattern-breakers ARE converging faster than 2022 projected. Estonia 1.74 → 1.51, Uruguay 1.78 → 1.44 (down 0.34!) consistent with late-transition completion catching up. Late-transition timing is the dominant explanation on 2024 data.',
          },
          {
            label: 'B — Structural metapopulation differences',
            verdict: 'partial',
            reasoning: 'CASE-SPECIFIC ONLY. Estonia/Latvia/Uruguay all fell substantially — metapopulation didn\'t save them. Only Oman maintained (1.91 → 1.91); Israel-as-supplement at 2.31 is the strongest signal but wasn\'t in the pattern-breaker quartet. General structural-features framing fails the 2024 test.',
          },
          {
            label: 'C — Narrative architecture differences',
            verdict: 'partial',
            reasoning: 'MIXED. Israel 2.31 is the strongest single empirical signal in the dataset. Oman state-tribal narrative survives. Estonia/Latvia post-Soviet identity narrative did not save them. Narrative architecture works case-specifically — strongest where existential or theologically grounded.',
          },
          {
            label: 'D — Immigration / rescue effect',
            verdict: 'partial',
            reasoning: 'CONFIRMED FOR SPECIFIC CASES. Oman, France 1.65, US 1.64 all show rescue-effect signal. Doesn\'t generalize.',
          },
        ],
        synthesis: 'Late-transition timing (A) is the dominant explanation on 2024 data. The "structural features" framing of H-3v3 was too broad — it conflated refugia (case-specific, real, but narrow), narrative architecture (case-specific, strongest where existential), generalized metapopulation (empirically not supported), and late-transition timing (does the bulk of the work). H-3v3 retires; the narrower H-13 / H-16 / H-2v2 hypotheses get incidental empirical support.',
      },
      counterNarrative: {
        title: 'Counter-Narrative (re-evaluated)',
        question: 'What would the opposite story look like, and is there evidence for it now?',
        candidates: [
          {
            label: '1 — Pattern-breakers are early-cliff, not pattern-breakers',
            strength: 'high',
            reasoning: 'EMPIRICALLY DEMONSTRATED. Pattern-breakers DID fall significantly between revisions (0.20-0.34). They aren\'t pattern-breakers in the structural-protection sense; they\'re slower-cliff cases. This counter-narrative was MEDIUM in original run, now upgraded to HIGH.',
          },
          {
            label: '2 — Pattern-breakers actively retain protective features',
            strength: 'partial',
            reasoning: 'CASE-SPECIFIC ONLY. Holds for Oman and Israel (supplement). Fails for Estonia/Latvia/Uruguay. Active-retention framing is a feature of specific cultural-religious continuity, not a general property of pattern-breakers.',
          },
          {
            label: '3 — The 0.54 gap was data-artifact (threshold-boundary)',
            strength: 'high',
            reasoning: 'VINDICATED — empirically demonstrated. The original Phase A run\'s most cautious critique was exactly right. Small projection-methodology changes between revisions did flip the verdict from "falsifier-triggered" to "inconclusive." The threshold-boundary fragility is now empirical fact.',
          },
        ],
        synthesis: 'The single most important methodological lesson from this run pair: threshold-boundary verdicts in projection-based hypotheses must be flagged as conditional on the data revision used. Future Phase A runs on threshold-boundary cases should be re-run automatically when the underlying data revision updates.',
      },
      outsideView: {
        title: 'Outside View (re-evaluated)',
        question: 'What reference classes would predict for this question on 2024 data?',
        candidates: [
          {
            label: 'A — Demographic transition theory (Notestein, Caldwell)',
            prediction: 'Late-transition convergence to a floor; floor varies by country but timing is the dominant signal.',
            implication: 'The 2024 convergence trend is exactly what DT theory predicts.',
            tilt: 'partial-support',
          },
          {
            label: 'B — Eco metapopulation theory (Hanski, Levins)',
            prediction: 'Structured metapopulations have higher floors than panmictic populations.',
            implication: 'Substrate transfer holds for cases with strong refugia (Oman). General framing weakens — average pattern-breaker doesn\'t show metapopulation-protection in 2024 data.',
            tilt: 'partial-support',
          },
          {
            label: 'C — Cultural evolution theory (Henrich, Boyd, Richerson)',
            prediction: 'Cultural traits supporting fertility persist in some lineages despite economic pressure.',
            implication: 'Holds for Israel + Oman where cultural-religious continuity is intact. Doesn\'t save secularized European cases.',
            tilt: 'partial-support',
          },
          {
            label: 'D — Hysteresis / regime-shift (Walker, Holling)',
            prediction: 'Countries that haven\'t crossed the cultural-regime threshold maintain different basin-of-attraction.',
            implication: 'Inconclusive verdict zone is consistent with multiple basins-of-attraction operating. Hysteresis is neither supported nor weakened cleanly by 2024 data.',
            tilt: 'partial-support',
          },
        ],
        synthesis: 'Reference-class consensus shifts toward partial support for late-transition-timing as the dominant explanation, with refugia and narrative architecture as case-specific moderators rather than general counter-explanations. Original outside-view ("3 of 4 tilt against late-transition") becomes "1 supports, 3 partial-support-with-case-specificity, 0 neutral."',
      },
    },
  },
  {
    id: 'h-3v2-2026-05-05',
    hypothesisId: 'H-Continuum-3v2',
    hypothesisTitle: 'Late-demographic-transition timing as the actual mechanism',
    runDate: '2026-05-05',
    verdict: 'survived-respecified',
    keyDataPoint: 'UN WPP 2022 gap: 0.54 (over threshold 0.5). 2024 revision live: 0.45 (inconclusive — verdict moved).',
    summary: 'Pattern-breaker median 2050 TFR ~1.76 vs trapped-cluster median ~1.22 (2022 data). Threshold-boundary verdict means counter-narrative #3 (data-artifact concern) is the strongest single critique. Three-voice red-team tilts verdict toward conjoint structural features (refugia + narrative + metapopulation) rather than late-transition timing alone. UPDATE 2026-05-05 PM: live UN WPP 2024 pull (scripts/pull-un-wpp.js) shows pattern-breaker median 1.48 vs trapped 1.03 → gap 0.45, sitting in the inconclusive zone. The data-artifact critique is empirically demonstrated; the respec to H-3v3 was prematurely motivated. Recommended next: re-run Phase A on 2024 data as a new discrete run.',
    respecifiedInto: 'H-Continuum-3v3',
    liveDataUpdate: {
      revisionDate: '2026-05-05',
      revision: 'UN WPP 2024',
      patternBreakerMedian: 1.48,
      trappedMedian: 1.03,
      gap: 0.45,
      verdict: 'inconclusive',
      interpretation: 'Verdict moved from threshold-boundary falsifier-triggered (2022) to inconclusive (2024). Late-transition timing is less wounded than the original Phase A run concluded. H-3v3 respec motivation is weakened.',
    },
    artifact: 'docs/CONTINUUM_H_3V2_PHASE_A_RUN.md',
    superseded: true,
    supersededBy: 'h-3v2-rerun-2024-2026-05-05',
    modules: {
      rootCause: {
        title: 'Root-Cause Engine',
        question: 'What is the actual cause of the maintained 0.54 gap?',
        candidates: [
          {
            label: 'A — Late-transition timing (the registered claim)',
            verdict: 'partial',
            reasoning: 'Explains some pattern-breakers (Uruguay continuing slow decline) but not Estonia\'s apparent floor at 1.7.',
          },
          {
            label: 'B — Structural metapopulation differences',
            verdict: 'plausible',
            reasoning: 'Estonia + Latvia rural cultural-religious communities preserved (per H-16). Trapped cluster countries are panmictic mega-cities. Within-country TFR variance correlates with national floor.',
          },
          {
            label: 'C — Narrative architecture differences',
            verdict: 'plausible',
            reasoning: 'Pattern-breakers have ongoing-existential or ongoing-mission narratives (Estonia post-Soviet identity, Oman state-tribal, Uruguay Catholic-progressive). Trapped cluster is arrival-completed-without-successor.',
          },
          {
            label: 'D — Immigration / rescue effect',
            verdict: 'partial',
            reasoning: 'Oman especially has substantial immigrant fertility contribution. Doesn\'t explain Estonia/Latvia gap.',
          },
        ],
        synthesis: 'Conjoint causes B + C with A as secondary. "Structural features" framing of original H-3 was right in spirit; specific causes are refugia + narrative + metapopulation, not the four-feature bundle of retired H-3.',
      },
      counterNarrative: {
        title: 'Counter-Narrative',
        question: 'What would the opposite story look like, and is there evidence for it?',
        candidates: [
          {
            label: '1 — Pattern-breakers are early-cliff, not pattern-breakers',
            strength: 'medium',
            reasoning: 'Estonia/Latvia post-Soviet baby-boom cohorts aging out; Uruguay slow 30-year decline; Oman TFR fell from 8+ in 1980 to ~2.5 today. The "slower-cliff" framing has empirical traction.',
          },
          {
            label: '2 — Pattern-breakers actively retain protective features',
            strength: 'medium-high',
            reasoning: 'The 4 retained features (rural cultural-religious continuity, regional metapopulation, narrative-mission architecture) the trapped cluster lost during their late-transition. Trapped cluster\'s loss is what produced the cliff.',
          },
          {
            label: '3 — The 0.54 gap is data-artifact (THRESHOLD-BOUNDARY)',
            strength: 'high',
            reasoning: 'UN WPP variants span ~0.3 to ~0.8 gap depending on assumptions. Statistics-Korea projections vary by 0.2+ depending on methodology. The 0.54 sits right at the 0.5 cutoff — small projection-methodology choices flip the verdict.',
          },
        ],
        synthesis: 'Counter-narrative #3 (data-artifact) is the strongest single critique. The verdict\'s robustness is questionable; sensitivity to projection-methodology choices is high.',
      },
      outsideView: {
        title: 'Outside View',
        question: 'What reference classes would predict for this question?',
        candidates: [
          {
            label: 'A — Demographic transition theory (Notestein, Caldwell)',
            prediction: 'Late-transition convergence to a floor, but the floor varies by country based on structural features. ~1.5–1.7 floor for most cases.',
            implication: 'Supports gap-maintenance at ~0.5+ — partial support for late-transition framing.',
            tilt: 'partial-support',
          },
          {
            label: 'B — Eco metapopulation theory (Hanski, Levins)',
            prediction: 'Structured metapopulations have higher floors than panmictic populations at equivalent stress.',
            implication: 'Supports gap-maintenance via structural difference; weakens late-transition-alone claim.',
            tilt: 'against',
          },
          {
            label: 'C — Cultural evolution theory (Henrich, Boyd, Richerson)',
            prediction: 'Cultural traits supporting fertility (kin obligation, religious narrative, family-as-unit) persist in some lineages despite economic pressure.',
            implication: 'Strongly weakens late-transition timing claim; reinforces H-2v2 / H-11 cultural-layer-necessity.',
            tilt: 'against',
          },
          {
            label: 'D — Hysteresis / regime-shift (Walker, Holling)',
            prediction: 'Countries that haven\'t crossed the cultural-regime threshold (anti-natalism normalization) maintain different basin-of-attraction.',
            implication: 'Strongly weakens late-transition claim; reinforces H-Continuum-18 hysteresis hypothesis.',
            tilt: 'against',
          },
        ],
        synthesis: 'Three of four reference classes (B, C, D) tilt against late-transition-as-dominant. Only reference class A is partially supportive, and even there the support is for "convergence to a floor" rather than "convergence to the same floor."',
      },
    },
  },
];

// ─── Next moves panel ─────────────────────────────────────────
export const NEXT_MOVES = {
  priorityAnchors: [
    {
      id: 'h-3v2',
      label: 'Most-tractable next test',
      hypothesisId: 'H-Continuum-3v2',
      detail: 'Late-transition projection test. Public data, no new instruments, falsifies cleanly.',
      status: 'completed',
      result: 'SURVIVED-RESPECIFIED → H-3v3 proposed',
    },
    {
      id: 'h-15',
      label: 'Highest-stakes test (KEYSTONE)',
      hypothesisId: 'H-Continuum-15',
      detail: 'Cross-substrate mechanism transfer. Decides whether V1 → V2 unified-instrument thesis is empirically supported. Run via H-17 (Allee) + H-18 (hysteresis) sub-tests.',
      status: 'pending',
    },
    {
      id: 'h-16',
      label: 'Highest-leverage discovery test',
      hypothesisId: 'H-Continuum-16',
      detail: 'Demographic refugia. Tests under-explored eco-to-human transfer with concrete intervention-design implications. If confirmed, mainstream pronatalism framing is theoretically wrong-framed.',
      status: 'pending',
    },
  ],
  phase7Wiring: [
    {
      id: 'p7-1',
      order: 1,
      label: 'UN WPP formal pull',
      detail: 'Trivial — 1 small script. Public CSV download. Unblocks H-3v3 + future projection-based hypotheses.',
      cost: '1 session',
      blocks: 'H-3v3, future projections',
    },
    {
      id: 'p7-2',
      order: 2,
      label: 'OECD Family Database ingestion',
      detail: 'H-10 / H-11 dependency. Public API. Pronatalist spending + family policies per country.',
      cost: '1 session',
      blocks: 'H-10, H-11',
    },
    {
      id: 'p7-3',
      order: 3,
      label: 'Korean sub-regional TFR + cooperative-breeding-density composite',
      detail: 'H-17 Allee threshold dependency. Substantial — 2-3 sessions across data pull + composite construction. Surface to Tim before starting.',
      cost: '2-3 sessions',
      blocks: 'H-17',
      flag: 'critical-tbd',
    },
    {
      id: 'p7-4',
      order: 4,
      label: 'GDELT 2.0 ingestion + sentiment pipeline',
      detail: 'Tier 4 cultural source. Single most-valuable Tier 4 source for cascade integration plan.',
      cost: '3-4 sessions',
      blocks: 'H-11, H-18, H-3v3, H-12',
    },
    {
      id: 'p7-5',
      order: 5,
      label: 'Google Trends API integration',
      detail: 'Fast cultural-attitude proxies. Supplements GDELT.',
      cost: '1 session',
      blocks: 'cultural attitude tracking',
    },
    {
      id: 'p7-6',
      order: 6,
      label: 'Sub-national TFR — Italy / Japan / Eurostat / Iran',
      detail: 'Enables H-3v3 metapopulation predictor + cross-country replication for H-17.',
      cost: '4-5 sessions',
      blocks: 'H-3v3, H-17 cross-country',
    },
    {
      id: 'p7-7',
      order: 7,
      label: 'ESA listings + MSC certifications + IUCN press archive',
      detail: 'Eco substrate Tier 3 institutional sources.',
      cost: '1-2 sessions',
      blocks: 'H-12 eco substrate testing',
    },
    {
      id: 'p7-8',
      order: 8,
      label: 'Curated literature review on species-cultural-attitude shifts',
      detail: 'Tim-led, Claude-supported. Yellowstone wolf / bald eagle / etc. cultural reframing arcs.',
      cost: '5-10 Tim-led sessions',
      blocks: 'H-15 case-set construction',
    },
  ],
  criticalTBDs: [
    {
      id: 'h17-composite',
      hypothesisId: 'H-Continuum-17',
      label: 'Korean cooperative-breeding-density composite proxy',
      detail: 'Multi-gen household % + community childcare access + peer-parent density per sub-region. KOSIS sub-regional TFR is direct-pull but the composite proxy needs targeted secondary-source work + judgment calls.',
    },
  ],
  decisionPoints: [
    {
      id: 'phase-6',
      label: 'Phase 6 schema decisions',
      detail: 'Substrate-agnostic schema design (substrate column, arrest_classification, metapopulation_structure, refugia_present, human_reflexive_layer_engaged). ~1 session, planning + decision only. Tim should be in on these decisions rather than Claude-default.',
    },
    {
      id: 'h-3v3-promotion',
      label: 'Promote H-Continuum-3v3 to active',
      detail: 'Currently in `proposed` status. Three-voice review (Code, Claude, Tim) needed before promotion. Most components have data sources via existing dossier infrastructure; metapopulation predictor needs Phase 5/6 source-stack work.',
    },
  ],
};
